import React from "react";

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[RootErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
  }

  private reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private copy = () => {
    const { error, errorInfo } = this.state;
    const text = [
      `URL: ${window.location.href}`,
      `UA: ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
      error ? `Error: ${error.name}: ${error.message}` : "",
      error?.stack ? `Stack:\n${error.stack}` : "",
      errorInfo?.componentStack ? `Component stack:${errorInfo.componentStack}` : "",
    ].filter(Boolean).join("\n\n");
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      // ignore
    }
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    if (!hasError) return this.props.children;

    // Inline styles only — do not rely on Tailwind/Theme since those may have failed.
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          backgroundColor: "#0b0b0f",
          color: "#f5f5f7",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 720,
            width: "100%",
            background: "#16161c",
            border: "1px solid #2a2a33",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
          }}
        >
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            La aplicación no se pudo iniciar
          </h1>
          <p style={{ marginTop: 8, color: "#a1a1aa", fontSize: 14 }}>
            Se ha capturado un error global (incluyendo posibles fallos en
            ThemeProvider u otros proveedores). Puedes reintentar, recargar o
            copiar el diagnóstico.
          </p>

          <div
            style={{
              marginTop: 16,
              background: "#0f0f15",
              border: "1px solid #2a2a33",
              borderRadius: 8,
              padding: 12,
              maxHeight: 320,
              overflow: "auto",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
              color: "#e4e4e7",
            }}
          >
            {error ? (
              <>
                <div style={{ color: "#f87171", fontWeight: 600 }}>
                  {error.name}: {error.message}
                </div>
                {error.stack && (
                  <div style={{ marginTop: 8, opacity: 0.85 }}>{error.stack}</div>
                )}
                {errorInfo?.componentStack && (
                  <>
                    <div style={{ marginTop: 12, fontWeight: 600 }}>
                      Component stack
                    </div>
                    <div style={{ opacity: 0.85 }}>{errorInfo.componentStack}</div>
                  </>
                )}
              </>
            ) : (
              <span style={{ color: "#a1a1aa" }}>Sin detalles disponibles.</span>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
            <button
              onClick={this.reset}
              style={btnStyle("#3b82f6", "#fff")}
            >
              Reintentar
            </button>
            <button
              onClick={() => window.location.reload()}
              style={btnStyle("transparent", "#f5f5f7", "#2a2a33")}
            >
              Recargar página
            </button>
            <button
              onClick={this.copy}
              style={btnStyle("transparent", "#f5f5f7", "#2a2a33")}
            >
              Copiar diagnóstico
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              style={btnStyle("transparent", "#a1a1aa", "#2a2a33")}
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function btnStyle(bg: string, color: string, border = "transparent"): React.CSSProperties {
  return {
    background: bg,
    color,
    border: `1px solid ${border === "transparent" ? bg : border}`,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  };
}
